import type { Maybe, Response, StoreThis } from '@/types'
import type { Profile, ProfileFormType } from '@/types/customer/users'

import { ref } from 'vue'

import { defineStore } from 'pinia'

import { unProxy, handleErrors, clone } from '@/helpers'
import { goal } from '@/plugins/metrika'

const resource = 'profile'
const storeId = `customer-${resource}`

export const useCustomerProfileStore = defineStore(storeId, () => {
  const profile = ref<Maybe<Profile>>(null)

  const isLoading = ref(false)

  async function fetchProfile(this: StoreThis) {
    isLoading.value = true

    try {
      const {
        data: { data, success, errors },
      }: Response<Profile> = await this.rest.get(`/c/${resource}`)

      if (!success && errors) throw Error(handleErrors(errors))

      profile.value = data
      if (data.new) {
        goal('registration')
      }
    } catch (e) {
      console.error(`[${storeId}/fetchProfile]`, resource, e)
    } finally {
      isLoading.value = false
    }
  }

  async function edit(this: StoreThis, payload: ProfileFormType) {
    let error = false
    let validationErrors: Record<string, string[]> = {}
    let otherError = ''

    isLoading.value = true

    try {
      const {
        data: { success, errors, data },
      }: Response<Profile> = await this.rest.put(
        `/c/${resource}`,
        clone(unProxy(payload))
      )

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }

      profile.value = data
    } catch (e) {
      error = true
      console.error(`[${storeId}/edit]`, resource, e)
    } finally {
      isLoading.value = false
    }

    return {
      error,
      otherError,
      validationErrors,
    }
  }

  function reset() {
    profile.value = null
  }

  return {
    profile,
    isLoading,

    fetchProfile,
    edit,
    reset,
  }
})
